import {useState, useEffect} from 'react';
import postCreateTaskFlow from '../functions/API/postCreateTaskFlow.js'
import postUpdateTaskFlow from '../functions/API/postUpdateTaskFlow.js'
import { toast } from '../lib/toast.js';

// One flow option: a self-describing card rather than a bare checkbox in a list.
// The whole card is the hit target (the input is nested, so the label associates
// implicitly) and a checked option is tinted so the enabled set reads at a glance.
function OptionToggle({ name, label, description, checked, onChange }) {
    return (
        <label
            className={`flex cursor-pointer gap-3 rounded-lg border p-3 transition-colors ${
                checked
                    ? 'border-blue-600 bg-blue-50/60 dark:border-blue-500 dark:bg-blue-900/20'
                    : 'border-gray-200 bg-white hover:border-gray-300 dark:border-zinc-700 dark:bg-zinc-800/40 dark:hover:border-zinc-600'
            }`}
        >
            <input
                type="checkbox"
                onChange={onChange}
                name={name}
                id={name}
                checked={checked}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 dark:border-zinc-600 text-blue-700 focus:ring-blue-700"
            />
            <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-900 dark:text-white">{label}</span>
                <span className="mt-0.5 block text-xs leading-relaxed text-gray-500 dark:text-gray-400">{description}</span>
            </span>
        </label>
    );
}

export default function NewTaskFlowForm({setVisible, refresh, refreshValue, editMode = false, existingData = null}) {
    const [newTaskFlow, setNewTaskFlow] = useState({
        id: "",
        name: "",
        description: "",
        tenant_requestable: false,
        is_onboarding: false,
        admin_assignable: false,
        has_deadline: false,
        deadline_days: "",
        deadline_type: "calendar",
        help_url: "",
        help_text: "",
        is_cancellable: false,
        cancellation_reasons: [],
        cancellation_requires_approval: false,
        enforce_admin_step_order: false,
    })

    useEffect(() => {
        if (editMode && existingData) {
            setNewTaskFlow({
                id: existingData.id || "",
                name: existingData.name || "",
                description: existingData.description || "",
                tenant_requestable: existingData.tenant_requestable || false,
                is_onboarding: existingData.is_onboarding || false,
                admin_assignable: existingData.admin_assignable || false,
                has_deadline: existingData.has_deadline || false,
                deadline_days: existingData.deadline_days || "",
                deadline_type: existingData.deadline_type || "calendar",
                help_url: existingData.help_url || "",
                help_text: existingData.help_text || "",
                is_cancellable: existingData.is_cancellable || false,
                cancellation_reasons: existingData.cancellation_reasons || [],
                cancellation_requires_approval: existingData.cancellation_requires_approval || false,
                enforce_admin_step_order: existingData.enforce_admin_step_order || false,
            })
        }
    }, [editMode, existingData])

    async function createNewTaskFlow(){
        // Validate required fields
        if (!newTaskFlow.name || !newTaskFlow.description) {
            toast.error('Name and Description are required.');
            return;
        }

        // Validate deadline fields if has_deadline is true
        if (newTaskFlow.has_deadline && !newTaskFlow.deadline_days) {
            toast.error('Deadline days is required when deadline is enabled.');
            return;
        }

        // Validate cancellation reasons if cancellable
        if (newTaskFlow.is_cancellable && newTaskFlow.cancellation_reasons.filter(r => r.trim() !== '').length === 0) {
            toast.error('At least one cancellation reason is required when cancellation is enabled.');
            return;
        }

        const body = {
            name: newTaskFlow.name,
            description: newTaskFlow.description,
            tenant_requestable: newTaskFlow.tenant_requestable,
            is_onboarding: newTaskFlow.is_onboarding,
            admin_assignable: newTaskFlow.admin_assignable,
            has_deadline: newTaskFlow.has_deadline,
            deadline_days: newTaskFlow.has_deadline ? newTaskFlow.deadline_days : null,
            deadline_type: newTaskFlow.has_deadline ? newTaskFlow.deadline_type : null,
            help_url: newTaskFlow.help_url || null,
            help_text: newTaskFlow.help_text || null,
            is_cancellable: newTaskFlow.is_cancellable,
            cancellation_reasons: newTaskFlow.is_cancellable ? newTaskFlow.cancellation_reasons.filter(r => r.trim() !== '') : null,
            cancellation_requires_approval: newTaskFlow.is_cancellable ? newTaskFlow.cancellation_requires_approval : false,
            enforce_admin_step_order: newTaskFlow.enforce_admin_step_order,
        }

        // Add ID for update operations
        if (editMode) {
            body.id = newTaskFlow.id;
        }

        const apiFunction = editMode ? postUpdateTaskFlow : postCreateTaskFlow;
        const successMessage = editMode ? 'Task flow updated successfully.' : 'Task flow created successfully.';

        const TaskFlowResponse = await apiFunction(body);
        if(TaskFlowResponse.response.ok){
            refresh(refreshValue+1);
            toast.success(successMessage);
            setVisible(false);
        }else{
            toast.error('Sorry an error occurred. Try again later.');
        }
    }

    function handleChange (event) {
        let statechange = event.target.name;
        let value = event.target.type === 'checkbox'
            ? event.target.checked
            : event.target.value;
        setNewTaskFlow({...newTaskFlow, [statechange]: value})
    }

    return (
        <form>
            <div className="space-y-12">
                <div>
                    <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-white">
                        {editMode ? 'Edit task flow' : 'Create new task flow'}
                    </h2>
                    <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-400">Task flows define process workflows for onboarding, requests, and administrative tasks</p>
                </div>

                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-3 md:col-span-1">
                    <div className="sm:col-span-3">
                        <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                            Name
                        </label>
                        <div className="mt-2">
                            <input
                            required
                            type="text"
                            onChange={handleChange}
                            value={newTaskFlow.name}
                            name="name"
                            id="name"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                            />
                        </div>
                    </div>

                    <div className="sm:col-span-3">
                        <label htmlFor="description" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                            Description
                        </label>
                        <div className="mt-2">
                            <textarea
                            required
                            onChange={handleChange}
                            value={newTaskFlow.description}
                            name="description"
                            id="description"
                            rows="3"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                            />
                        </div>
                    </div>

                    <div className="sm:col-span-3 space-y-5">
                        <div>
                            <h3 className="text-sm font-semibold leading-6 text-gray-900 dark:text-white">Options</h3>
                            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                How this flow can be started and how it behaves once it is running.
                            </p>
                        </div>

                        <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                Availability
                            </p>
                            <div className="mt-2 space-y-3">
                                <OptionToggle
                                    name="tenant_requestable"
                                    label="Tenant Requestable"
                                    description="Customers can start this flow themselves from the portal."
                                    checked={newTaskFlow.tenant_requestable}
                                    onChange={handleChange}
                                />
                                <OptionToggle
                                    name="admin_assignable"
                                    label="Admin Assignable"
                                    description="Administrators can raise this flow on a customer's behalf."
                                    checked={newTaskFlow.admin_assignable}
                                    onChange={handleChange}
                                />
                                <OptionToggle
                                    name="is_onboarding"
                                    label="Is Onboarding"
                                    description="Used for new customer onboarding invites rather than day-to-day requests."
                                    checked={newTaskFlow.is_onboarding}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                Behaviour
                            </p>
                            <div className="mt-2 space-y-3">
                                <OptionToggle
                                    name="has_deadline"
                                    label="Has Deadline"
                                    description="Gives every request created from this flow a due date."
                                    checked={newTaskFlow.has_deadline}
                                    onChange={handleChange}
                                />
                                <OptionToggle
                                    name="is_cancellable"
                                    label="Allow Customer Cancellation"
                                    description="Customers can cancel their own request before it completes."
                                    checked={newTaskFlow.is_cancellable}
                                    onChange={handleChange}
                                />
                                <OptionToggle
                                    name="enforce_admin_step_order"
                                    label="Enforce Step Order For Administrators"
                                    description="Administrators must work their steps in flow order. Steps set to not require the previous step stay available. Applies to requests created after this is enabled."
                                    checked={newTaskFlow.enforce_admin_step_order}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                    </div>

                    {newTaskFlow.has_deadline && (
                        <div className="sm:col-span-3 rounded-lg border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800/40 p-4">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white">Deadline</h4>
                            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                How long a request from this flow has to be completed.
                            </p>
                            <div className="mt-3 space-y-4">
                            <div>
                                <label htmlFor="deadline_days" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                                    Deadline Days
                                </label>
                                <div className="mt-2">
                                    <input
                                        required={newTaskFlow.has_deadline}
                                        type="number"
                                        onChange={handleChange}
                                        value={newTaskFlow.deadline_days}
                                        name="deadline_days"
                                        id="deadline_days"
                                        min="1"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="deadline_type" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                                    Deadline Type
                                </label>
                                <div className="mt-2">
                                    <select
                                        required={newTaskFlow.has_deadline}
                                        onChange={handleChange}
                                        name="deadline_type"
                                        id="deadline_type"
                                        value={newTaskFlow.deadline_type}
                                        className="pl-2 block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                                    >
                                        <option value="calendar">Calendar Days</option>
                                        <option value="working">Working Days</option>
                                    </select>
                                </div>
                            </div>
                            </div>
                        </div>
                    )}

                    {newTaskFlow.is_cancellable && (
                        <div className="sm:col-span-3 space-y-4 rounded-lg border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800/40 p-4">
                            <div>
                                <h4 className="text-sm font-medium text-gray-900 dark:text-white">Cancellation Reasons</h4>
                                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                    Define the reasons a customer can select when cancelling.
                                </p>
                            </div>
                            <div className="space-y-2">
                                {newTaskFlow.cancellation_reasons.map((reason, index) => (
                                    <div key={index} className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={reason}
                                            onChange={(e) => {
                                                const updated = [...newTaskFlow.cancellation_reasons];
                                                updated[index] = e.target.value;
                                                setNewTaskFlow({...newTaskFlow, cancellation_reasons: updated});
                                            }}
                                            placeholder="Enter a cancellation reason"
                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const updated = newTaskFlow.cancellation_reasons.filter((_, i) => i !== index);
                                                setNewTaskFlow({...newTaskFlow, cancellation_reasons: updated});
                                            }}
                                            className="text-red-500 hover:text-red-700 text-sm font-medium shrink-0"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setNewTaskFlow({...newTaskFlow, cancellation_reasons: [...newTaskFlow.cancellation_reasons, '']});
                                    }}
                                    className="text-sm text-blue-700 hover:text-blue-500 font-medium"
                                >
                                    + Add Reason
                                </button>
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    onChange={handleChange}
                                    name="cancellation_requires_approval"
                                    id="cancellation_requires_approval"
                                    checked={newTaskFlow.cancellation_requires_approval}
                                    className="h-4 w-4 rounded border-gray-300 dark:border-zinc-600 text-blue-700 focus:ring-blue-700"
                                />
                                <label htmlFor="cancellation_requires_approval" className="ml-3 block text-sm font-medium text-gray-900 dark:text-white">
                                    Require Admin Approval
                                </label>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 ml-7">
                                When enabled, cancellation requests must be approved by an admin before taking effect.
                            </p>
                        </div>
                    )}

                    <div className="sm:col-span-3">
                        <label htmlFor="help_url" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                            Help URL (optional)
                        </label>
                        <div className="mt-2">
                            <input
                                type="url"
                                onChange={handleChange}
                                value={newTaskFlow.help_url}
                                name="help_url"
                                id="help_url"
                                placeholder="https://..."
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                            />
                        </div>
                    </div>

                    <div className="sm:col-span-3">
                        <label htmlFor="help_text" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                            Help Text (optional)
                        </label>
                        <div className="mt-2">
                            <textarea
                                onChange={handleChange}
                                value={newTaskFlow.help_text}
                                name="help_text"
                                id="help_text"
                                rows="3"
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                            />
                        </div>
                    </div>

                </div>
            </div>

            <div className="mt-6 flex items-center justify-start gap-x-6">
                <button
                type="button"
                onClick={()=>{createNewTaskFlow()}}
                className="rounded-md bg-blue-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700"
                >
                {editMode ? 'Update' : 'Create'}
                </button>
                <button type="button" onClick={()=>{setVisible(false)}} className="text-sm font-semibold leading-6 text-gray-900 dark:text-white">
                Cancel
                </button>
            </div>
        </form>
    );
}
